import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraBadge } from '../ensigns/badge';
import { SpecraButton } from '../ensigns/button';
import { SpecraLink } from '../ensigns/link';
import { SpecraSpinner } from '../ensigns/spinner';
import { SpecraSurface } from '../ensigns/surface';
import { SpecraText } from '../ensigns/typography';
import { SpecraEntityCell } from './entity-cell';
import { SpecraInlineBanner } from './inline-banner';
import { SpecraKeyValueRow } from './key-value-row';
const NOUN = {
    CONTACT: 'person',
    COMPANY: 'company',
    GOVERNMENT_ORG: 'government office',
    VEHICLE: 'contract vehicle'
};
const MAX_EVIDENCE = 3;
const MAX_DETAILS = 6;
function announcement(state, linked) {
    if (state === 'linked') return linked ? `Linked to ${linked.name}` : 'Linked';
    if (state === 'created') return 'Added to the registry';
    if (state === 'deferred') return 'Waiting in the review queue';
    return '';
}
/** "Is this John Smith (DISA)?": confirm, pick, decline or add a registry match. */ export function SpecraEntityMatchConfirm({ candidates, className, confirmLabel, createLabel, density = 'card', disabled = false, errorMessage, kind, linked, observedContext, observedLabel, observedSource, onConfirm, onCreateNew, onDecline, onDefer, onDismiss, question: questionOverride, readOnly = false, renderLink, showUnresolvedBadge = false, size = 'sm', state = 'ask', 'data-testid': testIdProp }) {
    const testId = testIdProp ?? Testids.registryMatch.prompt;
    const noun = NOUN[kind];
    const questionId = React.useId();
    const rootRef = React.useRef(null);
    const card = density === 'card';
    const pending = state === 'pending';
    const off = disabled || pending;
    // After "No", the next question's first button takes focus.
    const previousState = React.useRef(state);
    const phaseRef = React.useRef(state === 'declined' ? 'declined' : 'ask');
    React.useEffect(()=>{
        if (state === 'declined' && previousState.current !== 'declined') {
            rootRef.current?.querySelector('[data-match-actions] button')?.focus();
        }
        previousState.current = state;
    }, [
        state
    ]);
    const link = (name, href)=>href ? /*#__PURE__*/ _jsx(SpecraLink, {
            href: href,
            renderLink: renderLink,
            children: name
        }) : name;
    const evidenceBadges = (c)=>{
        const evidence = (c.evidence ?? []).slice(0, MAX_EVIDENCE);
        if (evidence.length === 0 && !c.readOnlyReference) return null;
        return /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex flex-wrap items-center gap-[var(--space-1)]",
            children: [
                c.readOnlyReference ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "outline",
                    children: "Reference"
                }) : null,
                evidence.map((e)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: e
                    }, e))
            ]
        });
    };
    const button = (action, label, onClick, variant, testSuffix, itemId)=>/*#__PURE__*/ _jsx(SpecraButton, {
            "data-action": action,
            "data-item-id": itemId,
            "data-testid": `${testId}-${testSuffix}`,
            disabled: off,
            onClick: onClick,
            size: size,
            variant: variant,
            children: label
        }, action);
    const resolved = state === 'linked' || state === 'created';
    const several = candidates.length > 1;
    const single = candidates.length === 1 ? candidates[0] : undefined;
    // pending/error are transient: keep showing the phase the user acted from
    // (a create started after "No" must not fall back to the candidate question).
    if (state === 'ask' || state === 'declined') phaseRef.current = state;
    const transient = state === 'pending' || state === 'error';
    const declinedPhase = state === 'declined' || transient && phaseRef.current === 'declined';
    const declined = declinedPhase || candidates.length === 0;
    const observed = observedContext ? `${observedLabel} (${observedContext})` : observedLabel;
    let question;
    if (resolved || state === 'deferred') question = observedLabel;
    else if (declinedPhase && candidates.length > 0) question = `“${observedLabel}” isn't linked to the registry yet.`;
    else if (candidates.length === 0) question = `“${observedLabel}” isn't in the registry yet.`;
    else if (questionOverride) question = questionOverride;
    else if (single) question = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "Is this ",
            link(single.name, single.href),
            single.context ? ` (${single.context})` : '',
            "?"
        ]
    });
    else question = `Which ${noun} is “${observed}”?`;
    const actions = [];
    if (!readOnly && !resolved && state !== 'deferred') {
        if (!declined && single) {
            actions.push(button('confirm', confirmLabel ?? 'Yes, link', ()=>onConfirm(single.id), 'default', 'confirm', single.id));
            if (onDecline) actions.push(button('decline', 'No', onDecline, 'secondary', 'decline'));
        } else if (!declined && several && onDecline) {
            actions.push(button('decline', 'None of these', onDecline, 'ghost', 'decline'));
        }
        if (declined && onCreateNew) {
            actions.push(button('create', createLabel ?? `Add as new ${noun}`, onCreateNew, 'default', 'create'));
        }
        if (onDefer) actions.push(button('defer', 'Decide later', onDefer, 'ghost', 'defer'));
        if (onDismiss) actions.push(button('dismiss', 'Remove', onDismiss, 'ghost', 'dismiss'));
    }
    const detailRows = (c)=>card && c.details?.length ? /*#__PURE__*/ _jsx("dl", {
            className: "m-0 flex flex-col",
            children: c.details.slice(0, MAX_DETAILS).map((d)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    dense: true,
                    label: d.label,
                    value: d.value,
                    variant: "fact"
                }, d.label))
        }) : null;
    const candidateList = several && !readOnly && !declined && !resolved && state !== 'deferred' ? /*#__PURE__*/ _jsx("ul", {
        className: "m-0 flex w-full list-none flex-col gap-[var(--space-1-5)] p-0",
        children: candidates.map((c)=>/*#__PURE__*/ _jsxs("li", {
                "data-item-id": c.id,
                "data-testid": `${testId}-candidate`,
                children: [
                    /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        description: card ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                c.context,
                                c.context && (c.evidence?.length || c.readOnlyReference) ? ' ' : null,
                                evidenceBadges(c)
                            ]
                        }) : c.context,
                        title: link(c.name, c.href),
                        trailing: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                            "aria-label": `This one: ${c.name}${c.context ? ` (${c.context})` : ''}`,
                            "data-action": "confirm",
                            "data-item-id": c.id,
                            "data-testid": `${testId}-confirm`,
                            disabled: off,
                            onClick: ()=>onConfirm(c.id),
                            size: "xs",
                            variant: "secondary",
                            children: "This one"
                        })
                    }),
                    detailRows(c)
                ]
            }, c.id))
    }) : null;
    const outcome = resolved ? /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-[var(--space-1-5)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraBadge, {
                "data-testid": `${testId}-linked`,
                variant: "success",
                children: state === 'linked' ? 'Linked' : 'Added to the registry'
            }),
            linked ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                children: link(linked.name, linked.href)
            }) : null
        ]
    }) : state === 'deferred' ? /*#__PURE__*/ _jsx(SpecraText, {
        size: "meta",
        tone: "muted",
        children: "Waiting in the review queue."
    }) : null;
    const body = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("span", {
                className: cn('flex min-w-0 flex-wrap items-center gap-[var(--space-1-5)]', !card && 'flex-1'),
                children: [
                    showUnresolvedBadge ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "warning",
                        children: "Unresolved"
                    }) : null,
                    readOnly && candidates.length > 0 && !resolved ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "caption",
                        tone: "muted",
                        children: observedLabel
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: cn('min-w-0', card && 'font-medium'),
                        id: questionId,
                        size: card ? 'body-sm' : 'caption',
                        children: question
                    }),
                    !card ? outcome : null
                ]
            }),
            card && observedSource ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: observedSource
            }) : null,
            card && single && !readOnly && !declined && !resolved && state !== 'deferred' ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    evidenceBadges(single),
                    detailRows(single)
                ]
            }) : null,
            card ? outcome : null,
            candidateList,
            state === 'error' && errorMessage ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: errorMessage
            }) : null,
            actions.length > 0 || pending ? /*#__PURE__*/ _jsxs("span", {
                className: "flex flex-wrap items-center gap-[var(--space-1-5)]",
                "data-match-actions": "",
                children: [
                    actions,
                    pending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                        label: "Saving",
                        size: "sm"
                    }) : null
                ]
            }) : null,
            /*#__PURE__*/ _jsx("span", {
                "aria-live": "polite",
                className: "sr-only",
                children: announcement(state, linked)
            })
        ]
    });
    const common = {
        'aria-busy': pending || undefined,
        'aria-labelledby': questionId,
        'data-kind': kind,
        'data-state': state,
        'data-testid': testId,
        ref: rootRef,
        role: 'group'
    };
    if (card) {
        return /*#__PURE__*/ _jsx(SpecraSurface, {
            ...common,
            className: cn('flex flex-col gap-[var(--space-2)] p-[var(--space-3)]', className),
            radius: "md",
            children: body
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        ...common,
        className: cn('flex min-w-0 flex-wrap items-center gap-[var(--space-2)]', className),
        children: body
    });
}
SpecraEntityMatchConfirm.displayName = 'SpecraEntityMatchConfirm';
